import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Check, Loader2, ShieldCheck, Lock } from "lucide-react";

export default function OrderApproval() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [authed, setAuthed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const load = useCallback(() => api.get(`/orders/approval/${token}`).then((r) => setData(r.data))
    .catch((e) => setError(apiError(e.response?.data?.detail) || "Approval link not found")), [token]);
  useEffect(() => { load(); }, [load]);

  const setupPassword = async () => {
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try { await api.post(`/orders/approval/${token}/set-password`, { password: pw }); setAuthed(true); toast.success("Password set — you're logged in"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not set password"); }
    finally { setBusy(false); }
  };
  const loginExisting = async () => {
    if (!pw) { toast.error("Enter your password"); return; }
    setBusy(true);
    try { await api.post(`/auth/login`, { email: data.customer.email, password: pw }); setAuthed(true); toast.success("Logged in"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Invalid password"); }
    finally { setBusy(false); }
  };
  const approve = async () => {
    setBusy(true);
    try { await api.post(`/orders/approval/${token}/approve`); setDone(true); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not approve"); }
    finally { setBusy(false); }
  };

  if (error) return <div className="max-w-xl mx-auto px-4 py-24 text-center text-zinc-400" data-testid="order-approval-error">{error}</div>;
  if (!data) return <div className="max-w-xl mx-auto px-4 py-24 text-center text-zinc-500"><Loader2 className="animate-spin mx-auto" /></div>;

  const money = (n) => `$${(n || 0).toFixed(2)}`;

  if (done || data.approved) return (
    <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="order-approval-done">
      <div className="w-14 h-14 rounded-full bg-[#10b981]/15 border border-[#10b981]/40 flex items-center justify-center mx-auto"><Check className="text-[#10b981]" /></div>
      <h1 className="font-display text-2xl font-black text-white mt-5">Order approved — thank you!</h1>
      <p className="text-zinc-400 mt-3">We've emailed you deposit &amp; payment instructions{data.requires_deposit ? ` (50% deposit: ${money(data.deposit_amount)})` : ""}. Send your proof of payment and we'll begin production once it's confirmed.</p>
      <Link to="/track"><Button data-testid="approval-track-btn" className="mt-6 bg-[#ff6a13] hover:bg-[#ff8a3d] rounded-full h-11 px-6 font-semibold">Track your order</Button></Link>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12" data-testid="order-approval-page">
      <div className="flex items-center gap-2 text-[#ff6a13] font-mono-p text-xs uppercase tracking-widest"><ShieldCheck size={14} />Order approval</div>
      <h1 className="font-display text-3xl font-black text-white mt-2">Review your order</h1>
      <p className="text-zinc-400 mt-2">Hi {data.customer.name}, please review the details below and approve to get started.</p>

      <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5 mt-6" data-testid="approval-order-summary">
        <p className="text-xs font-mono-p uppercase tracking-wider text-zinc-500">Order {data.order_number}</p>
        <div className="mt-3 divide-y divide-zinc-800">
          {data.items.map((it, i) => (
            <div key={i} className="flex items-center gap-3 py-2.5" data-testid={`approval-item-${i}`}>
              {it.image_url && <img src={fileUrl(it.image_url)} alt="" className="w-10 h-10 rounded object-contain bg-[#0c0d10] border border-zinc-800 shrink-0" />}
              <span className="text-white text-sm flex-1">{it.name}</span>
              <span className="text-zinc-500 text-sm">×{it.quantity}</span>
              <span className="text-white text-sm font-semibold w-20 text-right">{money((it.unit_price || 0) * (it.quantity || 1))}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-zinc-800 space-y-1 text-sm">
          {data.delivery_fee > 0 && <div className="flex justify-between text-zinc-400"><span>Delivery</span><span>{money(data.delivery_fee)}</span></div>}
          {data.overnight_fee > 0 && <div className="flex justify-between text-zinc-400"><span>Overnight</span><span>{money(data.overnight_fee)}</span></div>}
          {data.rush_fee > 0 && <div className="flex justify-between text-zinc-400"><span>Rush</span><span>{money(data.rush_fee)}</span></div>}
          <div className="flex justify-between text-white font-bold text-base"><span>Total</span><span>{money(data.total)}</span></div>
          {data.requires_deposit && <div className="flex justify-between text-[#22c3d6]"><span>50% deposit due after approval</span><span>{money(data.deposit_amount)}</span></div>}
        </div>
        {data.admin_note && <p className="text-sm text-zinc-400 mt-3 border-l-2 border-[#ff6a13]/60 pl-3">{data.admin_note}</p>}
      </div>

      {!authed ? (
        <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5 mt-5" data-testid="approval-auth-box">
          <div className="flex items-center gap-2 text-white font-semibold"><Lock size={15} className="text-[#ff6a13]" />{data.needs_password ? "Set a password to continue" : "Log in to approve"}</div>
          <p className="text-sm text-zinc-400 mt-1">{data.needs_password ? "Create a password for your account so you can approve this order and track future orders." : `Enter the password for ${data.customer.email}.`}</p>
          <div className="mt-4 space-y-3">
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder={data.needs_password ? "New password (min 6 chars)" : "Your password"} data-testid="approval-password" className="bg-[#1e222d] border-zinc-700 text-white" />
            {data.needs_password && <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirm password" data-testid="approval-password-confirm" className="bg-[#1e222d] border-zinc-700 text-white" />}
            <Button onClick={data.needs_password ? setupPassword : loginExisting} disabled={busy} data-testid="approval-auth-submit" className="w-full bg-[#ff6a13] hover:bg-[#ff8a3d] rounded-full h-11 font-semibold">
              {busy ? <Loader2 size={16} className="animate-spin" /> : (data.needs_password ? "Set password & continue" : "Log in")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-5">
          <Button onClick={approve} disabled={busy} data-testid="approval-approve-btn" className="w-full bg-[#10b981] hover:bg-[#0ea371] rounded-full h-12 font-semibold text-base">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <><Check size={18} className="mr-1.5" />Approve this order</>}
          </Button>
          <p className="text-xs text-zinc-500 text-center mt-2 font-mono-p">By approving, you accept this order. Deposit/payment instructions will follow by email.</p>
        </div>
      )}
    </div>
  );
}
